import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Shield } from 'lucide-react';

export default function AdminLoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, logout } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email, password);
            // Check if user is a property manager
            if (user.role !== 'property_manager') {
                logout(); // Log out non-admin users
                toast.error('Access denied. This login is for Property Managers only.');
                setLoading(false);
                return;
            }
            toast.success('Welcome back, Admin!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="admin-login-page">
            {/* Background pattern */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-blue-500/10 blur-3xl" />
                <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-blue-500/10 via-teal-500/10 to-emerald-500/10 blur-3xl" />
            </div>

            <div className="w-full max-w-md relative z-10 animate-fade-in">
                <div className="flex items-center justify-center gap-3 mb-8">
                    <img src="/logo.png" alt="House2home" className="w-12 h-12 rounded-xl shadow-lg object-cover" />
                    <div>
                        <h1 className="text-2xl font-bold text-white">House2home</h1>
                        <p className="text-sm text-slate-300">Admin Portal</p>
                    </div>
                </div>

                <Card className="border-0 shadow-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700">
                    <CardHeader className="space-y-1 pb-4">
                        <div className="flex items-center justify-center mb-2">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                                <Shield className="w-6 h-6 text-white" />
                            </div>
                        </div>
                        <CardTitle className="text-2xl text-center text-white">Admin Access</CardTitle>
                        <CardDescription className="text-center text-slate-400">
                            Property Manager login only
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-300">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="admin@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                                        required
                                        data-testid="admin-email-input"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-300">Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                                        required
                                        data-testid="admin-password-input"
                                    />
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                                disabled={loading}
                                data-testid="admin-login-button"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Signing in...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        Admin Sign in
                                        <ArrowRight className="w-4 h-4" />
                                    </span>
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 space-y-2 text-center text-sm">
                            <Link 
                                to="/forgot-password" 
                                className="text-emerald-400 hover:text-emerald-300 font-medium block"
                                data-testid="admin-forgot-password-link"
                            >
                                Forgot your password?
                            </Link>
                            <div>
                                <span className="text-slate-500">Not an admin? </span>
                                <Link 
                                    to="/login" 
                                    className="text-emerald-400 hover:text-emerald-300 font-medium"
                                    data-testid="regular-login-link"
                                >
                                    Regular login
                                </Link>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <p className="text-center text-xs text-slate-500 mt-6">
                    © 2024 House2home Property Management. All rights reserved.
                </p>
            </div>
        </div>
    );
}
